/**
 * Utility object for cloning and immutably updating objects and arrays.
 */
const clone = {
  /** 
   * Symbol used to indicate that a value should be deleted when using `clone.set`.
   */
  DELETE_VALUE: Symbol('DELETE_VALUE'),

  /**
   * Creates a shallow copy of the given value.
   * 
   * - If the value is an array, a new array is returned.
   * - If the value is an object, a new object is returned.
   * 
   * @template T - The type of the value to clone.
   * @param {T} value - The object or array to shallow clone.
   * @returns {T} A shallow copy of the input value.
   */
  shallow<T extends object | any[]>(value: T): T {
    return Array.isArray(value) ? ([...value] as T) : {...value};
  },

  /**
   * Immutably sets one or multiple values on an object using dot-separated keys.
   * 
   * If a single object argument is provided, it sets all key-value pairs in that object.
   * Nested objects/arrays are cloned shallowly to preserve immutability.
   * 
   * Special handling:
   * - If the value is a function, it is called with the current value at the path.
   * - If the value is `clone.DELETE_VALUE`, the key will be removed from the object/array.
   * 
   * @param {object} object - The object to update.
   * @param {...[object] | any[]} args - array of the form [key1: string, value1: any, ...], or a single object of updates.
   * @returns {object} A new object with the updates applied.
   */
  set(object: object, ...args: [object] | any[]): object {
    if (args.length === 1 && (typeof args[0] === 'object')) {
      return clone.set(object, ...Object.entries(args[0]).reduce((_, [k, v]) => {
        _.push(k, v);

        return _;
      }, [] as any[]));
    }

    const clonedObject = clone.shallow(object);

    while(args.length > 1){
      const selector = args.shift();
      let value = args.shift();
      const keys = selector.split('.');

      let currentClone = clonedObject as any;

      while (keys.length > 1){
        let key = keys.shift();

        if (Array.isArray(currentClone)) {
          key = key | 0;
        }
    
        const nextObject = currentClone[key];
        const nextClone = clone.shallow(nextObject);
    
        currentClone[key] = nextClone;
        currentClone = nextClone;
      }
    
      const key = keys.shift();
            
      if (typeof value === 'function') {
        value = value(currentClone[key]);
      }

      if (value === clone.DELETE_VALUE) {
        if (Array.isArray(currentClone)) {
          currentClone.splice(key, 1);
        } else {
          delete currentClone[key];
        }
      } else {
        currentClone[key] = value;
      }
    }
    
    return clonedObject;
  },

  /**
   * Creates a shallow copy of an array and performs a splice operation immutably.
   * 
   * @template T - The type of the array.
   * @param {T} array - The array to clone and modify.
   * @param {number} start - The starting index for the splice.
   * @param {number} [deleteCount=1] - Number of elements to remove.
   * @param {...any[]} insert - Elements to insert at the start index.
   * @returns {T} A new array with the splice operation applied.
   */
  splice<T extends any[]>(array: T, start: number, deleteCount: number = 1, ...insert: any[]){
    const clone = array.slice();
    clone.splice(start, deleteCount, ...insert);

    return clone;
  },
};


export default clone;